import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import AccessTimeOutlined from "@mui/icons-material/AccessTimeOutlined";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import ShieldOutlined from "@mui/icons-material/ShieldOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import "./ServicePolicyGate.css";

type MaintenancePolicy = {
  active: boolean;
  title: string;
  reason: string;
  ends_at: string | null;
  support_url: string;
};

const POLICY_API = (
  process.env.REACT_APP_GODFATHER_PUBLIC_URL || "https://api.godfather.tazty.in"
).replace(/\/$/, "");

export default function ServicePolicyGate({ children }: PropsWithChildren) {
  const [maintenance, setMaintenance] = useState<MaintenancePolicy | null>(null);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch(
        `${POLICY_API}/public/v1/applications/seller-web/policy?platform=web`,
        { cache: "no-store" },
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      setMaintenance(payload?.data?.maintenance?.active ? payload.data.maintenance : null);
    } catch (error) {
      console.warn("Seller availability policy is temporarily unavailable", error);
      setMaintenance(null);
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = window.setInterval(refresh, 60_000);
    const onVisibility = () => document.visibilityState === "visible" && void refresh();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  if (!maintenance) return <>{children}</>;

  return (
    <main className="service-policy-screen" role="alert" aria-live="assertive">
      <section className="service-policy-shell">
        <header>
          <span className="service-policy-mark"><ShieldOutlined sx={{ fontSize: 18 }} /> TAZTY SELLER CONTROL</span>
          <span className="service-policy-code">STATUS / MAINTENANCE</span>
        </header>
        <div className="service-policy-body">
          <WarningAmberOutlined sx={{ fontSize: 34 }} />
          <p className="service-policy-kicker">PLANNED SERVICE WINDOW</p>
          <h1>{maintenance.title || "Seller services are under maintenance"}</h1>
          <p>{maintenance.reason || "We are completing an important platform update."}</p>
          {maintenance.ends_at && (
            <div className="service-policy-time">
              <AccessTimeOutlined sx={{ fontSize: 16 }} /> Expected recovery: {new Date(maintenance.ends_at).toLocaleString("en-IN")}
            </div>
          )}
          <div className="service-policy-actions">
            <button onClick={() => void refresh()}><RefreshOutlined sx={{ fontSize: 16 }} /> Check again</button>
            {maintenance.support_url && <a href={maintenance.support_url}>Contact support</a>}
          </div>
        </div>
        <footer>Live status is controlled by Tazty Godfather operations.</footer>
      </section>
    </main>
  );
}
